import React, { useState, useEffect } from 'react';
import { useTradingCall } from '../contexts/TradingCallContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AccumulatedDataPersistence } from '../services/accumulatedDataPersistence';
import type { TradingCall } from '../types/TradingCall';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import ToolState from './mmec/ToolState';
import './TradingCallTracker.ds.css';

const TABS = ['overview', 'calls', 'performance', 'data'] as const;

const tileStyle: React.CSSProperties = {
  background: 'var(--elev2)',
  border: '1px solid var(--hair)',
  borderRadius: 'var(--radius-md, 10px)',
  padding: 12,
};
const heroStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
  fontFamily: 'var(--font-mono)',
  fontVariantNumeric: 'tabular-nums',
};
const subStyle: React.CSSProperties = { fontSize: 12, color: 'var(--muted)', marginTop: 4 };
const numStyle: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' };
const thStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--muted)',
  letterSpacing: '.03em',
  textAlign: 'start',
};
const thNumStyle: React.CSSProperties = { ...thStyle, textAlign: 'end' };
const tdStyle: React.CSSProperties = { padding: '6px 10px', textAlign: 'start' };
const tdNumStyle: React.CSSProperties = { ...tdStyle, textAlign: 'end', ...numStyle };
const rowStyle: React.CSSProperties = { borderBottom: '1px solid var(--hair)' };

const TradingCallTracker: React.FC = () => {
  const { activeCalls, closedCalls, wallet } = useTradingCall();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState<'overview' | 'calls' | 'performance' | 'data'>('overview');
  const [] = useState(() => new AccumulatedDataPersistence());
  const [accumulatedData] = useState<any[]>([]);
  const [] = useState<any[]>([]);

  const isArabic = language === 'ar';

  const translations = {
    title: isArabic ? 'تتبع التداول التجريبي' : 'Paper Trading Tracker',
    overview: isArabic ? 'نظرة عامة' : 'Overview',
    calls: isArabic ? 'الصفقات' : 'Calls',
    performance: isArabic ? 'الأداء' : 'Performance',
    data: isArabic ? 'البيانات' : 'Data',
    activeCalls: isArabic ? 'الصفقات النشطة' : 'Active Calls',
    closedCalls: isArabic ? 'الصفقات المغلقة' : 'Closed Calls',
    totalPnL: isArabic ? 'إجمالي الربح/الخسارة' : 'Total P&L',
    winRate: isArabic ? 'معدل الربح' : 'Win Rate',
    totalTrades: isArabic ? 'إجمالي الصفقات' : 'Total Trades',
    averageWin: isArabic ? 'متوسط الربح' : 'Average Win',
    averageLoss: isArabic ? 'متوسط الخسارة' : 'Average Loss',
    maxDrawdown: isArabic ? 'أقصى انخفاض' : 'Max Drawdown',
    sharpeRatio: isArabic ? 'نسبة شارب' : 'Sharpe Ratio',
    profitFactor: isArabic ? 'عامل الربح' : 'Profit Factor',
    accumulatedData: isArabic ? 'البيانات المتراكمة' : 'Accumulated Data',
    resetEvents: isArabic ? 'أحداث إعادة التعيين' : 'Reset Events',
    symbol: isArabic ? 'الرمز' : 'Symbol',
    status: isArabic ? 'الحالة' : 'Status',
    entryPrice: isArabic ? 'سعر الدخول' : 'Entry Price',
    currentPrice: isArabic ? 'السعر الحالي' : 'Current Price',
    pnl: isArabic ? 'الربح/الخسارة' : 'P&L',
    confidence: isArabic ? 'الثقة' : 'Confidence',
    timestamp: isArabic ? 'الوقت' : 'Timestamp',
    strategy: isArabic ? 'الاستراتيجية' : 'Strategy',
    sentiment: isArabic ? 'المشاعر' : 'Sentiment',
    noActiveCalls: isArabic ? 'لا توجد صفقات نشطة' : 'No active calls',
    noClosedCalls: isArabic ? 'لا توجد صفقات مغلقة' : 'No closed calls',
    exportData: isArabic ? 'تصدير البيانات' : 'Export Data',
    importData: isArabic ? 'استيراد البيانات' : 'Import Data',
    sessionInfo: isArabic ? 'معلومات الجلسة' : 'Session Info',
    systemRestarts: isArabic ? 'إعادة تشغيل النظام' : 'System Restarts',
    dataRecords: isArabic ? 'سجلات البيانات' : 'Data Records',
    lastReset: isArabic ? 'آخر إعادة تعيين' : 'Last Reset',
    targetPrice: isArabic ? 'سعر الهدف' : 'Target Price',
    closePrice: isArabic ? 'سعر الإغلاق' : 'Close Price'
  };

  useEffect(() => {
    // DISABLED: Load accumulated data
    // const loadAccumulatedData = () => {
    //   try {
    //     const data = accumulatedDataPersistence.getAllAccumulatedData();
    //     setAccumulatedData(data);
    //   } catch (error) {
    //     console.error('Error loading accumulated data:', error);
    //   }
    // };

    // loadAccumulatedData();
    }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'var(--acc)';
      case 'TARGET_HIT': return 'var(--up)';
      case 'STOP_LOSS': return 'var(--down)';
      case 'EXPIRED': return 'var(--warn)';
      case 'CANCELLED': return 'var(--muted)';
      default: return 'var(--muted-2)';
    }
  };

  const getPnLColor = (pnl: number) => {
    return pnl >= 0 ? 'var(--up)' : 'var(--down)';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  // ── Design System preview (artboard web/trading-call-tracker): one table per list. ──
  const ds = isDesignSystemOn();
  const lang = isArabic ? 'ar' : 'en';
  const plText = (v: number) => (v >= 0 ? 'var(--up-text)' : 'var(--down-text)');
  const statusTone = (s: string) => (s === 'TARGET_HIT' ? 'mmec-tag--up' : s === 'STOP_LOSS' ? 'mmec-tag--down' : s === 'ACTIVE' ? 'mmec-tag--gold' : '');
  const renderDsTable = (rows: TradingCall[], closed: boolean) => (
    <div style={{ overflow: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)' }}>
      <table className="mmec-table">
        <thead>
          <tr>
            <th>{translations.timestamp}</th>
            <th>{translations.symbol}</th>
            <th>{translations.strategy}</th>
            <th style={{ textAlign: 'end' }}>{translations.entryPrice}</th>
            <th style={{ textAlign: 'end' }}>{closed ? translations.closePrice : translations.currentPrice}</th>
            <th style={{ textAlign: 'end' }}>{translations.targetPrice}</th>
            <th style={{ textAlign: 'end' }}>{translations.pnl} $</th>
            <th style={{ textAlign: 'end' }}>{translations.pnl} %</th>
            <th style={{ textAlign: 'end' }}>{translations.confidence}</th>
            {!closed ? <th /> : null}
            <th>{translations.status}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((call) => {
            const p = call.performance;
            const last = closed ? p?.closePrice || 0 : p?.currentPrice;
            const span = call.targetPrice - call.stopLoss;
            const prog = p && span ? Math.max(0, Math.min(1, ((p.currentPrice ?? call.entryPrice) - call.stopLoss) / span)) : null;
            return (
              <tr key={call.id}>
                <td className="mmec-mono" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(call.timestamp).toLocaleString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                <td className="mmec-mono" style={{ whiteSpace: 'nowrap' }}>{call.symbol}</td>
                <td style={{ color: 'var(--text-muted)' }}>{call.strategy.type} · {call.direction}</td>
                <td className="mmec-num">{formatCurrency(call.entryPrice)}</td>
                <td className="mmec-num">{last != null ? formatCurrency(last) : '—'}</td>
                <td className="mmec-num mmec-num--up">{formatCurrency(call.targetPrice)}</td>
                <td className="mmec-num" style={{ color: p ? plText(p.pnl) : 'var(--text-muted)', fontWeight: 600 }}>{p ? formatCurrency(p.pnl) : '—'}</td>
                <td className="mmec-num" style={{ color: p ? plText(p.pnl) : 'var(--text-muted)' }}>{p ? formatPercentage(p.pnlPercentage) : '—'}</td>
                <td className="mmec-num">{call.confidence}%</td>
                {!closed ? (
                  <td>
                    {prog != null ? (
                      <div style={{ position: 'relative', height: 8, width: 120, background: 'var(--surface-3)', borderRadius: 2 }} title={`${translations.entryPrice} → ${translations.targetPrice}`}>
                        <span style={{ position: 'absolute', insetInlineStart: 0, top: 0, bottom: 0, borderRadius: 2, background: p && p.pnl >= 0 ? 'var(--up)' : 'var(--down)', width: `${prog * 100}%` }} />
                      </div>
                    ) : null}
                  </td>
                ) : null}
                <td><span className={`mmec-tag ${statusTone(call.status)}`}>{call.status.replace(/_/g, ' ')}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div
      className={`p-4${ds ? ' mmec tct-root' : ''}`}
      dir={ds ? (isArabic ? 'rtl' : 'ltr') : undefined}
      style={{
        background: 'var(--elev)',
        border: '1px solid var(--hair)',
        borderRadius: 10,
        color: 'var(--txt)',
        fontFamily: 'var(--font-ui)',
        fontSize: 13,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold" style={{ fontSize: 18 }}>{translations.title}</h2>
        <div className="flex items-center gap-2">
          <div
            className="px-2"
            style={{
              height: 28,
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--elev2)',
              border: '1px solid var(--hair)',
              color: 'var(--muted)',
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            🔒 {isArabic ? 'نظام داخلي' : 'Internal System'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="mmec-seg mb-4"
        role="group"
        style={{ '--n': TABS.length, '--i': TABS.indexOf(activeTab) } as React.CSSProperties}
      >
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`mmec-seg-btn${activeTab === tab ? ' is-active' : ''}`}
          >
            {translations[tab]}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Active Calls */}
            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: ds ? 'var(--text)' : 'var(--acc)' }}>{activeCalls.length}</div>
              <div style={subStyle}>{translations.activeCalls}</div>
            </div>

            {/* Closed Calls */}
            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: ds ? 'var(--text)' : 'var(--up)' }}>{closedCalls.length}</div>
              <div style={subStyle}>{translations.closedCalls}</div>
            </div>

            {/* Total P&L */}
            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: ds ? plText(wallet.totalPnL) : getPnLColor(wallet.totalPnL) }}>
                {formatCurrency(wallet.totalPnL)}
              </div>
              <div style={subStyle}>{translations.totalPnL}</div>
            </div>

            {/* Win Rate */}
            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: ds ? 'var(--text)' : 'var(--up)' }}>
                {formatPercentage(wallet.performance.winRate)}
              </div>
              <div style={subStyle}>{translations.winRate}</div>
            </div>

            {/* Session Info */}
            <div className="col-span-2" style={tileStyle}>
              <h3 className="font-semibold mb-2" style={{ fontSize: 13 }}>{translations.sessionInfo}</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span style={{ color: 'var(--muted)' }}>{translations.systemRestarts}:</span>
                  <span style={numStyle}>{localStorage.getItem('system_restart_count') || '0'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span style={{ color: 'var(--muted)' }}>{translations.dataRecords}:</span>
                  <span style={numStyle}>{accumulatedData.length}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'calls' && ds && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="mmec-label">{translations.activeCalls} · <span className="mmec-mono">{activeCalls.length}</span></span>
              {activeCalls.length ? renderDsTable(activeCalls, false) : <ToolState kind="empty" lang={lang} title={translations.noActiveCalls} body="" />}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="mmec-label">{translations.closedCalls} · <span className="mmec-mono">{closedCalls.length}</span></span>
              {closedCalls.length ? renderDsTable(closedCalls.slice(0, 10), true) : <ToolState kind="empty" lang={lang} title={translations.noClosedCalls} body="" />}
            </div>
          </div>
        )}

        {activeTab === 'calls' && !ds && (
          <div className="space-y-4">
            {/* Active Calls */}
            <div>
              <h3 className="text-sm font-semibold mb-3">
                {translations.activeCalls} ({activeCalls.length})
              </h3>
              <div className="space-y-2">
                {activeCalls.map((call) => (
                  <div key={call.id} style={tileStyle}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold">{call.symbol}</div>
                        <div style={subStyle}>
                          {call.strategy.type} • {call.direction} • {translations.confidence}: {call.confidence}%
                        </div>
                        <div style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                          {translations.entryPrice}: {formatCurrency(call.entryPrice)} • {translations.targetPrice}: {formatCurrency(call.targetPrice)}
                        </div>
                      </div>
                      <div className="text-right" style={{ color: getStatusColor(call.status) }}>
                        <div className="font-semibold">{call.status}</div>
                        {call.performance && (
                          <div style={{ fontSize: 12, color: getPnLColor(call.performance.pnl), fontVariantNumeric: 'tabular-nums' }}>
                            {translations.pnl}: {formatCurrency(call.performance.pnl)} ({formatPercentage(call.performance.pnlPercentage)})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {activeCalls.length === 0 && (
                  <div className="text-center py-8" style={{ color: 'var(--muted)', fontSize: 13 }}>
                    {translations.noActiveCalls}
                  </div>
                )}
              </div>
            </div>

            {/* Closed Calls */}
            <div>
              <h3 className="text-sm font-semibold mb-3">
                {translations.closedCalls} ({closedCalls.length})
              </h3>
              <div className="space-y-2">
                {closedCalls.slice(0, 10).map((call) => (
                  <div key={call.id} style={tileStyle}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold">{call.symbol}</div>
                        <div style={subStyle}>
                          {call.strategy.type} • {call.direction} • {translations.confidence}: {call.confidence}%
                        </div>
                        <div style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                          {translations.entryPrice}: {formatCurrency(call.entryPrice)} • {translations.closePrice}: {formatCurrency(call.performance?.closePrice || 0)}
                        </div>
                      </div>
                      <div className="text-right" style={{ color: getStatusColor(call.status) }}>
                        <div className="font-semibold">{call.status}</div>
                        {call.performance && (
                          <div style={{ fontSize: 12, color: getPnLColor(call.performance.pnl), fontVariantNumeric: 'tabular-nums' }}>
                            {translations.pnl}: {formatCurrency(call.performance.pnl)} ({formatPercentage(call.performance.pnlPercentage)})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {closedCalls.length === 0 && (
                  <div className="text-center py-8" style={{ color: 'var(--muted)', fontSize: 13 }}>
                    {translations.noClosedCalls}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: ds ? 'var(--text)' : 'var(--acc)' }}>{wallet.performance.totalTrades}</div>
              <div style={subStyle}>{translations.totalTrades}</div>
            </div>

            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: ds ? 'var(--up-text)' : 'var(--up)' }}>{formatCurrency(wallet.performance.averageWin)}</div>
              <div style={subStyle}>{translations.averageWin}</div>
            </div>

            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: ds ? 'var(--down-text)' : 'var(--down)' }}>{formatCurrency(wallet.performance.averageLoss)}</div>
              <div style={subStyle}>{translations.averageLoss}</div>
            </div>

            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: ds ? 'var(--down-text)' : 'var(--warn)' }}>{formatCurrency(wallet.performance.maxDrawdown)}</div>
              <div style={subStyle}>{translations.maxDrawdown}</div>
            </div>

            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: 'var(--txt)' }}>{wallet.performance.sharpeRatio.toFixed(2)}</div>
              <div style={subStyle}>{translations.sharpeRatio}</div>
            </div>

            <div style={tileStyle}>
              <div style={{ ...heroStyle, color: 'var(--txt)' }}>{wallet.performance.profitFactor.toFixed(2)}</div>
              <div style={subStyle}>{translations.profitFactor}</div>
            </div>
          </div>
        )}

        {activeTab === 'data' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold mb-3">
                {translations.accumulatedData} ({accumulatedData.length})
              </h3>
              <div className="overflow-x-auto">
                <table className={`w-full${ds ? ' mmec-table' : ''}`} style={{ borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={rowStyle}>
                      <th style={thStyle}>{translations.symbol}</th>
                      <th style={thStyle}>{translations.status}</th>
                      <th style={thNumStyle}>Bid Count</th>
                      <th style={thNumStyle}>Ask Count</th>
                      <th style={thNumStyle}>Bid Volume</th>
                      <th style={thNumStyle}>Ask Volume</th>
                      <th style={thNumStyle}>{translations.confidence}</th>
                      <th style={thStyle}>{translations.lastReset}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accumulatedData.slice(0, 20).map((record) => (
                      <tr key={record.id} style={rowStyle}>
                        <td style={tdStyle}>{record.symbol}</td>
                        <td style={tdStyle}>{record.dataSource}</td>
                        <td style={tdNumStyle}>{record.accumulatedBidCount.toLocaleString()}</td>
                        <td style={tdNumStyle}>{record.accumulatedAskCount.toLocaleString()}</td>
                        <td style={tdNumStyle}>{record.accumulatedBidVolume.toLocaleString()}</td>
                        <td style={tdNumStyle}>{record.accumulatedAskVolume.toLocaleString()}</td>
                        <td style={tdNumStyle}>{record.confidence}%</td>
                        <td style={{ ...tdStyle, ...numStyle }}>{new Date(record.lastUpdated).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingCallTracker; 